import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  RECORD_STATUSES,
  US_STATES,
  getSettingsList,
  settingsFieldApplies,
  settingsRecordSchema,
} from '@app/shared';
import type {
  Paged,
  SettingsField,
  SettingsListDef,
  SettingsRecordDto,
  SettingsRefOptions,
} from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FormDialog } from '../../components/dialogs';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { StatusChip } from '../../components/StatusChip';
import { ApiError, api } from '../../lib/api';
import type { Query } from '../../lib/api';
import { formatCents, formatDateTime } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { RequirePermission } from '../auth/guards';

/** What the form holds while it is edited: text, or the chosen entries of a multiple select. */
type Draft = Record<string, string | string[]>;

const isMany = (f: SettingsField): boolean =>
  f.type === 'multiselect' || (f.type === 'ref' && Boolean(f.multiple));

const emptyDraft = (def: SettingsListDef): Draft => ({
  ...Object.fromEntries(def.fields.map((f) => [f.key, isMany(f) ? [] : ''])),
  status: 'Active',
});

function draftOf(def: SettingsListDef, record: SettingsRecordDto): Draft {
  const draft = emptyDraft(def);
  for (const f of def.fields) {
    const value = record.values[f.key];
    if (isMany(f)) draft[f.key] = Array.isArray(value) ? value : [];
    else if (f.type === 'money' || f.type === 'percent')
      draft[f.key] = typeof value === 'number' ? (value / 100).toFixed(2) : '';
    else draft[f.key] = value === null || value === undefined ? '' : String(value);
  }
  draft.status = record.status;
  return draft;
}

/**
 * The record as the API takes it: money in cents, numbers as numbers, empty as absent. A field
 * that does not apply (another shipper's block) is sent empty.
 */
function payloadOf(def: SettingsListDef, draft: Draft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of def.fields) {
    const value = settingsFieldApplies(f, draft) ? draft[f.key] : isMany(f) ? [] : '';
    if (Array.isArray(value)) out[f.key] = value;
    else if (f.type === 'money' || f.type === 'percent' || f.type === 'number') {
      const text = (value ?? '').trim();
      const n = Number(text);
      // Text that is not a number is sent as it is, so the schema names the field.
      // Dollars and percentages have two decimals on screen and none in the record.
      out[f.key] =
        text === ''
          ? null
          : Number.isFinite(n)
            ? f.type === 'number'
              ? n
              : Math.round(n * 100)
            : text;
    } else out[f.key] = value ?? '';
  }
  if (def.status) out.status = draft.status;
  return out;
}

function show(f: SettingsField, r: SettingsRecordDto): string {
  if (f.type === 'ref') return r.labels[f.key] ?? '';
  const value = r.values[f.key];
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (f.type === 'money' && typeof value === 'number') return formatCents(value);
  if (f.type === 'percent' && typeof value === 'number') return `${(value / 100).toFixed(2)}%`;
  return String(value);
}

function RecordDialog({
  def,
  record,
  onClose,
}: {
  def: SettingsListDef;
  record: SettingsRecordDto | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() =>
    record ? draftOf(def, record) : emptyDraft(def),
  );
  const [problems, setProblems] = useState<Record<string, string>>({});
  const featureOn = useFeatureReleased();
  // A field of a switch that is off keeps its stored value but is not shown.
  const shown = def.fields.filter(
    (f) => (!f.feature || featureOn(f.feature)) && settingsFieldApplies(f, draft),
  );
  const hasRefs = def.fields.some((f) => f.type === 'ref');
  const refs = useQuery({
    queryKey: ['settings-lists', def.key, 'refs'],
    queryFn: () => api.get<SettingsRefOptions>(`/settings-lists/${def.key}/refs`),
    enabled: hasRefs,
  });
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      record
        ? api.put<SettingsRecordDto>(`/settings-lists/${def.key}/${record.id}`, body)
        : api.post<SettingsRecordDto>(`/settings-lists/${def.key}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings-lists', def.key] }),
  });

  const set = (key: string, value: string | string[]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setProblems((p) => ({ ...p, [key]: '' }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = payloadOf(def, draft);
    const parsed = settingsRecordSchema(def).safeParse(body);
    if (!parsed.success) {
      const found: Record<string, string> = {};
      for (const issue of parsed.error.issues) found[String(issue.path[0])] ??= issue.message;
      setProblems(found);
      return;
    }
    try {
      await save.mutateAsync(body);
      notify.success(`${def.itemLabel} ${record ? 'updated' : 'added'}.`);
      onClose();
    } catch (err) {
      const fromServer =
        err instanceof ApiError
          ? Object.fromEntries(err.details.map((d) => [d.path, d.message]))
          : {};
      if (Object.keys(fromServer).length) setProblems(fromServer);
      else notify.error(err);
    }
  };

  const field = (f: SettingsField) => {
    const common = {
      key: f.key,
      label: f.label,
      required: f.required,
      error: Boolean(problems[f.key]),
      helperText: problems[f.key] || f.help,
    };
    if (f.type === 'select' || f.type === 'multiselect' || f.type === 'state' || f.type === 'ref') {
      const options: Array<[value: string, label: string]> =
        f.type === 'state'
          ? US_STATES.map(([code, name]) => [code, `${code} - ${name}`])
          : f.type === 'ref'
            ? (refs.data?.[f.key] ?? []).map((o) => [o.id, o.label])
            : f.options.map((o) => [o, o]);
      const many = isMany(f);
      return (
        <TextField
          {...common}
          select
          value={draft[f.key] ?? (many ? [] : '')}
          onChange={(e) => set(f.key, e.target.value as string | string[])}
          slotProps={{ select: { multiple: many } }}
          helperText={
            common.helperText ||
            (f.type === 'ref' && refs.isError ? `Could not load ${f.label}` : undefined)
          }
        >
          {many || f.required ? null : <MenuItem value="">--Select--</MenuItem>}
          {options.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    const text = f.type === 'text';
    return (
      <TextField
        {...common}
        value={draft[f.key] ?? ''}
        onChange={(e) => set(f.key, e.target.value)}
        multiline={text && f.multiline}
        minRows={text && f.multiline ? 3 : undefined}
        slotProps={{
          htmlInput: {
            maxLength: text ? (f.maxLength ?? 200) : f.type === 'zip' ? 5 : undefined,
            inputMode:
              f.type === 'money' || f.type === 'percent'
                ? 'decimal'
                : text || f.type === 'email'
                  ? undefined
                  : 'numeric',
          },
        }}
        sx={text && f.multiline ? { gridColumn: '1 / -1' } : undefined}
      />
    );
  };

  return (
    <FormDialog
      open
      title={record ? `Update ${def.itemLabel}` : def.addLabel}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      maxWidth={def.fields.length > 4 ? 'md' : 'sm'}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: def.fields.length > 1 ? '1fr 1fr' : '1fr',
            md: def.fields.length > 4 ? 'repeat(3, 1fr)' : undefined,
          },
        }}
      >
        {shown.map(field)}
        {def.status ? (
          <TextField
            select
            label="Status"
            value={draft.status}
            onChange={(e) => set('status', e.target.value)}
          >
            {RECORD_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Box>
    </FormDialog>
  );
}

/** The search form, the list and the add / update dialog of one settings list. */
export function SettingsListBody({ def }: { def: SettingsListDef }) {
  const [draft, setDraft] = useState({ q: '', status: '' });
  const [applied, setApplied] = useState<Query>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editing, setEditing] = useState<SettingsRecordDto | 'new' | null>(null);
  const records = useQuery({
    queryKey: ['settings-lists', def.key, applied, page, pageSize],
    queryFn: () =>
      api.get<Paged<SettingsRecordDto>>(`/settings-lists/${def.key}`, {
        ...applied,
        page,
        pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  const columns: Column<SettingsRecordDto>[] = [
    { key: '#', header: '#', width: 50, render: (r) => r.number },
    ...def.fields
      .filter((f) => f.listed !== false)
      .flatMap((f): Column<SettingsRecordDto>[] => [
        { key: f.key, header: f.label, render: (r) => show(f, r) },
        ...(def.derived ?? [])
          .filter((d) => d.after === f.key)
          .map((d) => ({
            key: d.key,
            header: d.header,
            render: (r: SettingsRecordDto) => r.labels[d.key] ?? '',
          })),
      ]),
    ...(def.status
      ? [
          {
            key: 'status',
            header: 'Status',
            render: (r: SettingsRecordDto) => <StatusChip status={r.status} />,
          },
        ]
      : []),
    { key: 'createdBy', header: 'Added By', render: (r) => r.createdBy?.loginId ?? '' },
    {
      key: 'updatedAt',
      header: 'Created/Updated Datetime',
      nowrap: true,
      render: (r) => formatDateTime(r.updatedAt),
    },
    {
      key: 'action',
      header: 'Action',
      render: (r) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => setEditing(r)}
            aria-label={`Edit ${def.itemLabel} ${r.number}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 1.5 }}>
        {def.note}
      </Alert>
      <Paper
        variant="outlined"
        component="form"
        noValidate
        aria-label={`${def.title} search`}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setApplied({ q: draft.q.trim(), status: draft.status });
          setPage(1);
        }}
        sx={{
          p: 1.5,
          mb: 1.5,
          display: 'grid',
          gap: 1.5,
          alignItems: 'center',
          // Search, status, Submit, free space, Add: one tight row on a desktop.
          gridTemplateColumns: {
            xs: '1fr',
            md: def.status ? '300px 170px auto 1fr auto' : '300px auto 1fr auto',
          },
        }}
      >
        <TextField
          label={def.searchLabel}
          value={draft.q}
          onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        {def.status ? (
          <TextField
            select
            label="Status"
            value={draft.status}
            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
          >
            <MenuItem value="">All</MenuItem>
            {RECORD_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <Button type="submit" variant="contained">
          Submit
        </Button>
        <Box sx={{ display: { xs: 'none', md: 'block' } }} />
        <Button variant="contained" onClick={() => setEditing('new')}>
          {def.addLabel}
        </Button>
      </Paper>
      <DataTable
        label={def.title}
        columns={columns}
        rows={records.data?.data}
        rowKey={(r) => r.id}
        loading={records.isFetching}
        error={records.error}
        onRetry={() => void records.refetch()}
        emptyText="No record found."
        page={page}
        pageSize={pageSize}
        total={records.data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSizeOptions={[10, 20, 50, 100]}
      />
      {editing ? (
        <RecordDialog
          // `key` gives each record its own form.
          key={editing === 'new' ? 'new' : editing.id}
          def={def}
          record={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Box>
  );
}

/**
 * One settings list as a page of its own (Shipper Settings, Payment Gateway, Tax Config and
 * the Other Settings lists). `back` adds the button that leads back to the landing page.
 */
export function SettingsListPage({
  listKey,
  back,
}: {
  listKey: string;
  back?: { to: string; label: string };
}) {
  const def = getSettingsList(listKey);
  if (!def) return <Alert severity="warning">This list does not exist.</Alert>;
  return (
    <RequirePermission code={def.code}>
      <PageHeader
        title={def.title}
        code={def.code}
        actions={
          back ? (
            <Button component={Link} to={back.to} variant="outlined">
              {back.label}
            </Button>
          ) : undefined
        }
      />
      {/* `key` gives each list its own search and paging. */}
      <SettingsListBody key={def.key} def={def} />
    </RequirePermission>
  );
}

/**
 * Tax Config (PC1203): the reference's list of tax-engine rules, and beside it Tax Rates,
 * this project's stand-in for the tax engine, which is what tax is worked out from.
 */
export function TaxConfigPage() {
  const [tab, setTab] = useState<'tax-config' | 'tax-rates'>('tax-config');
  const def = getSettingsList(tab)!;
  return (
    <RequirePermission code="PC1203">
      <PageHeader title="Tax Config" code="PC1203" />
      <Tabs
        value={tab}
        onChange={(_e, value: 'tax-config' | 'tax-rates') => setTab(value)}
        aria-label="Tax Config sections"
        sx={{ mb: 1.5 }}
      >
        <Tab value="tax-config" label="Tax Config" />
        <Tab value="tax-rates" label="Tax Rates" />
      </Tabs>
      <SettingsListBody key={def.key} def={def} />
    </RequirePermission>
  );
}
